/**
 * Which backends this console instance talks to.
 *
 * The console is a standalone static app on its own domains, so nothing can be
 * derived from a shared origin — the bases are picked by hostname:
 *
 *   crm.limosphere.com      → production backends
 *   anything else           → UAT backends (crm-uat.limosphere.com, localhost, previews)
 *
 * `VITE_CRM_BASE` / `VITE_DISPATCH_BASE` override both for local development
 * against a local sphere-crm.
 *
 * `dashboardBase` is the odd one out and is deliberately NOT defaulted by
 * hostname: it is the dispatch DASHBOARD's own web origin, used only to link a
 * booking this CRM made back to its dispatch row, and a wrong guess sends an
 * agent to a dashboard they are not signed into. The module EMBEDDED in the
 * dashboard resolves it from its own parent frame instead, which is exact and
 * covers the case that matters; a standalone console links only where the
 * build was given `VITE_DASHBOARD_BASE`, and otherwise renders the reference
 * as plain text. See screens/comms/booking/dispatchLink.ts.
 *
 * Pure — no window access — so the mapping is unit-testable; `config/index.ts`
 * applies it to the real location.
 */
export type FirebaseEnv = 'uat' | 'prod';

export interface Bases {
  /** sphere-crm base, e.g. https://backend-uat.limosphere.co/crm — REST is `${crmBase}/api/v1`. */
  crmBase: string;
  apiRoot: string;
  /** dispatch-be origin — the login + forgot-password endpoints live here. */
  dispatchBase: string;
  /** The dispatch dashboard's web origin, or null when the build named none. */
  dashboardBase: string | null;
  firebaseEnv: FirebaseEnv;
}

const PROD_HOSTNAME = 'crm.limosphere.com';

const UAT = {
  crmBase: 'https://backend-uat.limosphere.co/crm',
  dispatchBase: 'https://backend-uat.limosphere.co',
} as const;

const PROD = {
  crmBase: 'https://backend.limosphere.co/crm',
  dispatchBase: 'https://backend.limosphere.co',
} as const;

export interface BasesEnv {
  VITE_CRM_BASE?: string;
  VITE_DISPATCH_BASE?: string;
  VITE_DASHBOARD_BASE?: string;
}

const stripTrailingSlash = (value: string) => value.replace(/\/+$/, '');

export function deriveBases(hostname: string, env: BasesEnv = {}): Bases {
  const isProd = hostname === PROD_HOSTNAME;
  const defaults = isProd ? PROD : UAT;

  const crmBase = stripTrailingSlash(env.VITE_CRM_BASE || defaults.crmBase);
  const dispatchBase = stripTrailingSlash(
    env.VITE_DISPATCH_BASE || defaults.dispatchBase,
  );

  const dashboard = (env.VITE_DASHBOARD_BASE || '').trim();

  return {
    crmBase,
    apiRoot: `${crmBase}/api/v1`,
    dispatchBase,
    dashboardBase: dashboard ? stripTrailingSlash(dashboard) : null,
    firebaseEnv: isProd ? 'prod' : 'uat',
  };
}
